import { Input, InputText } from 'shared/ui/Inputs/Input';
import { LabelText } from 'shared/ui/label';
import { Typography } from 'shared/ui/typography';
import { Container } from './styles';
import { useHookFormMask } from 'use-mask-input';
import { useForm, Controller, ControllerRenderProps } from 'react-hook-form';
import { unmaskPhone } from 'shared/lib/string';
import { Textarea } from 'shared/ui/Inputs/textarea';
import { TagsOptions } from 'entities/tags';
import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'shared/lib/store';
import { selectPerson } from 'entities/person/model/selector';
import { Flex } from 'shared/ui/flex';
import { ButtonText } from 'shared/ui/buttons/buttonText';
import { createPerson, updatePerson } from 'entities/person/model/personThunk';
import { useModal } from 'shared/lib/modal';
import { MultiValue } from 'react-select';
import { Option } from 'shared/types';
import {
	Person,
	PersonFields,
	resetError,
	updatePersonBookItem,
  usePerson,
} from 'entities/person';
import { isRejectedWithValue, PayloadAction } from '@reduxjs/toolkit';
import { withAsyncActionFeedback } from 'shared/ui/asyncAction';
import { ButtonContained } from 'shared/ui/buttons/buttonContained';
import {
	getOrder,
	selectOrderDetail,
	updateOrderGroupItem,
	updatePersonDataInWaitingOrderItem,
} from 'entities/orders';
import { useLocation } from 'react-router-dom';
import { usePaginationFilter } from 'shared/lib/pagination';
import { TUpdateOrderType } from 'entities/person/lib/types';
import { CustomError } from 'shared/api';
import { toast } from 'react-toastify';

interface PersonFormProps {
	updatePersonBook?: boolean;
	updateOrder?: TUpdateOrderType;
  isCreate?: boolean;
  addGuestInOrder?: () => void;
}

type FormValues = {
  phone: string;
  name: string;
  notes: string;
};

export const PersonForm: FC<PersonFormProps> = ({
	updatePersonBook,
	updateOrder,
  isCreate = false,
  addGuestInOrder,
}) => {
  const ActionButtonWithFeedback = withAsyncActionFeedback(ButtonContained);

	const {
		register,
		control,
		setValue,
		formState: { errors },
		getValues,
		trigger,
    setError,
	} = useForm<FormValues>({
		defaultValues: {
			phone: '',
			name: '',
			notes: '',
		},
	});
  
  const { person: personContext, setPerson } = usePerson();
	const registerWithMask = useHookFormMask(register);
	const phoneMask = useRef('+7 (999) 999-99-99');
	const person = useAppSelector(selectPerson);
	const orderDetail = useAppSelector(selectOrderDetail);
	const dispatch = useAppDispatch();
	const { closeModal } = useModal();
	const [selectedTags, setSelectedTags] = useState<string[] | []>(
		person?.tags || [],
	);
	const { pathname } = useLocation();
	const closeForm = () => {
		dispatch(resetError());
		closeModal();
	};

	const paginationFilter = usePaginationFilter();

	const updatePersonBookState = (person: Person, fields: PersonFields) => {
		if (updatePersonBook) {
			dispatch(
				updatePersonBookItem({
					id: person.id,
					updates: fields,
				}),
			);

      if (personContext) {
        const newPerson = {
          ...personContext,
          name: getValues().name,
          phone: getValues().phone,
          notes: getValues().notes,
          tags: selectedTags,
        }

        setPerson(newPerson);
      }

			if (paginationFilter && paginationFilter?.filters?.search) {
				paginationFilter.fetchDataWithDispatch(
					paginationFilter.filters,
				);
			}
		}
	};

	const updatePersonDataInOrder = (fields: PersonFields) => {
		if (updateOrder && orderDetail?.id) {
			if (updateOrder === 'waitingOrders') {
				dispatch(
					updatePersonDataInWaitingOrderItem({
						id: orderDetail?.id,
						updates: fields,
					}),
				);
			} else if (updateOrder === 'groupOrders') {
				const personFields = { ...orderDetail.person, ...fields };
				dispatch(
					updateOrderGroupItem({
						id: orderDetail?.id,
						updates: {
							dt_start: orderDetail.dt_start,
							person: personFields,
						},
					}),
				);
			}
		}
	};

	const submit = async (): Promise<PayloadAction<unknown> | void> => {
    if (isCreate) {
			const isValid = await trigger(['phone', 'name']);
      if (isValid) {
				const fields: PersonFields = {
					phone: getValues('phone'),
					name: getValues('name'),
					notes: getValues('notes'),
					tags: selectedTags,
				};
        const created = await dispatch(createPerson(fields));
        if (isRejectedWithValue(created)) {
          const payload = created.payload as CustomError;
          if (payload.fields) {
            const keys = ['phone', 'name', 'notes'] as const;
            keys.forEach(key=>{
              if (payload.fields&&payload.fields[key]&&payload.fields[key][0]) {
                setError(key, {message: payload.fields[key][0]});
              }
            })
          }
          return;
        }

        toast.success('Гость успешно добавлен');

        closeModal();
        addGuestInOrder?.();
        return;
      }
    }
		if (person) {
			const isValid = await trigger(['phone', 'name']);
			if (isValid) {
				const fields: PersonFields = {
					phone: getValues('phone'),
					name: getValues('name'),
					notes: getValues('notes'),
					tags: selectedTags,
				};

				const result = await dispatch(
					updatePerson({
						id: person.id,
						body: fields,
					}),
				);

				if (!isRejectedWithValue(result)) {
					if (orderDetail && pathname !== '/contact-book') {
						dispatch(
							getOrder({
								id: orderDetail.id,
							}),
						);
					}

					updatePersonBookState(person, fields);
					updatePersonDataInOrder(fields);
				}

				return result;
			}
		}
	};

	const selectTags = (values: MultiValue<Option>) => {
		const tags = values.map(val => val.label);
		setSelectedTags(tags);
	};

	useEffect(() => {
		if (person&&!isCreate) {
			const phone = person.phone || '';
			const name = person.name || '';
			const notes = person.notes || '';

			setValue('phone', phone);
			setValue('name', name);
			setValue('notes', notes);
		}
	}, [person, setValue, isCreate]);

  const changeName = (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<FormValues, 'name'>,
  ) => {
    field.onChange(event);
  };
  
  const changeNotes = (
    event: ChangeEvent<HTMLTextAreaElement>,
    field: ControllerRenderProps<FormValues, 'notes'>,
  ) => {
    field.onChange(event);
  };

  return (
    <>
      <Container>
        <LabelText>Телефон</LabelText>
        <InputText
          {...registerWithMask('phone', [phoneMask.current], {
            onBeforeMask: (initialValue) => {
              if (initialValue.startsWith('89')) {
                return initialValue.replace('8', '');
              }

              if (initialValue.startsWith('7')) {
                return initialValue.replace('7', '');
              }

              return initialValue;
            },
            validate: value => {
              if (!value) {
                return true;
              }
              if (unmaskPhone(value).length !== 12) {
                return 'Некорректный номер телефона';
              }
            },
            onChange: event => {
              if (event.target.value.startsWith('+7 (89') || event.target.value.startsWith('+7 (7')) {
                setValue('phone', '+7 (9');
              }
            },
            onBeforePaste: (pastedValue) => {
              if (pastedValue.startsWith('8')) {
                return `+${pastedValue.replace('8', '7')}`;
              }

              if (!pastedValue.startsWith('+')) {
                return `+${pastedValue}`;
              }

              return pastedValue;

            },
          })}
          autoComplete="off"
          $textIndent="15px"
        />
        {errors.phone && (
          <Typography colorVariant={'danger'} mode="small">
            {errors.phone.message as string}
          </Typography>
        )}
      </Container>
      <Container>
        <LabelText>Имя и фамилия</LabelText>
        <Controller
          name="name"
          control={control}
          rules={{ required: 'Имя обязательно' }}
          render={({ field }) => (
            <>
              <Input 
                name={field.name}
                value={field.value}
                onChange={event => changeName(event, field)}
              />
              {errors.name && (
                <Typography
                  colorVariant={'danger'}
                  mode="small"
                >
                  {errors.name.message as string}
                </Typography>
              )}
            </>
          )}
        />
      </Container>
      <Container>
        <LabelText>Важная информация</LabelText>
        <Controller
          name="notes"
          control={control}
          render={({ field }) => <Textarea name={field.name} value={field.value} onChange={event => changeNotes(event, field)} />}
        />
      </Container>
      <Container>
        <LabelText>Теги</LabelText>
        <TagsOptions
          isForOrder={false}
          tagsOptions={person?.tags}
          onSelect={selectTags}
        />
      </Container>

      <div>
        <Flex $justify="center" $direction="column" $gap="10px">
          <ActionButtonWithFeedback
            onClick={submit}
            onSuccess={closeModal}
            successToastLabel="Изменения карточки гостя сохранены"
          >
            Сохранить
          </ActionButtonWithFeedback>
          <ButtonText color={'danger'} onClick={closeForm}>
            Отменить
          </ButtonText>
        </Flex>
      </div>
    </>
  );
};
